// The Calls page (web) — the Inbox pattern: a row of stat tiles, a filterable list
// on the left, the selected call's detail rail on the right, same ui components.
// ONE API feeds it, /comms/calls/ (lane C's CallSerializer contract — see
// callFormat.js for the field list; names confirmed, not guessed). Server-side
// filters: direction · outcome · user (rep) · start/end · queue=review&handled=false
// (needs follow-up). Client-side: has-recording and the name/number search, over
// the newest 200 rows the endpoint returns.
//
// At phone width (≤640) it is the mobile-shaped list the app shows: no tiles, a
// search + chip row, stacked rows, and the detail takes over the screen with Back.
//
// The customer window opens over this page in place — no route change.
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft, FileText, GraduationCap, MessageSquare, Mic, Phone, PhoneIncoming, PhoneMissed,
  PhoneOutgoing, SlidersHorizontal, UserPlus, Users, Voicemail as VmIcon,
} from "lucide-react";
import { fetchCallLogs, fetchCallDetail, fetchRecordingUrl, triageCall, wrapUpCall } from "../../../api/communications";
import { listTeamMembers } from "../../../api/jobsApi";
import ClickToCallButton from "../../Voice/ClickToCallButton";
import PageHeader from "../../ui/PageHeader";
import Button from "../../ui/button";
import Badge from "../../ui/badge";
import StatCard from "../../ui/StatCard";
import { Input, Select, Textarea, Field } from "../../ui/input";
import { Checkbox } from "../../ui/Toggle";
import { Table, TRow, TCell } from "../../ui/table";
import EmptyState from "../../ui/EmptyState";
import Skeleton from "../../ui/Skeleton";
import CardBoundary from "../../ui/CardBoundary";
import Sheet from "../../ui/Sheet";
import CallPlayer from "./CallPlayer";
import CallTranscript from "./CallTranscript";
import DialSheet from "./DialSheet";
import {
  contactName, counterpartyNumber, dateRangeParams, fmtDuration, fmtFull, fmtWhen, hasCoaching,
  hasRecording, hasTranscript, isChildLeg, matchesQuery, outcomeInfo,
  recordingNeedsFetch, recordingProcessing, repName, todayStats, wrapUpPayload,
} from "./callFormat";
import "./phone.css";

const CustomerPopup = lazy(() => import("../../Profile/CustomerPopup"));

// /comms/calls/ used to cost ~25 ms per row on t3 (each row presigned its recording
// URL — 200 rows was a 5 s first paint). Lane C's fix: a list row says `has_recording`
// and carries no URL; the rail presigns once on the detail read, or on Play via
// GET /comms/calls/<sid>/recording-url/. The paging below stays — it is what keeps
// the first paint small whatever a row costs.
const PAGE = 50; // first paint; older pages load on scroll via ?end=<oldest created_at>
const TODAY_LIMIT = 200;
const SLOW_AFTER_S = 2; // past this, the skeleton says how long it has been waiting
const EMPTY_FILTERS = { direction: "", outcome: "", user: "", range: "all", from: "", to: "", hasRecording: false, needsFollowUp: false };
const OUTCOMES = [
  ["answered", "Answered"], ["no_answer", "Missed / no answer"], ["voicemail", "Voicemail"], ["busy", "Busy"], ["failed", "Failed"],
];
const RANGES = [["all", "All time"], ["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["custom", "Custom…"]];

function useMedia(query) {
  const get = () => typeof window !== "undefined" && !!window.matchMedia?.(query).matches;
  const [on, setOn] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const fn = () => setOn(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", fn) : mq.addListener(fn);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", fn) : mq.removeListener(fn));
  }, [query]);
  return on;
}

const asList = (res) => (Array.isArray(res) ? res : res?.results || res?.calls || []);
const callKey = (c) => c?.call_sid || c?.id;
const linkedRecord = (c) => (c?.customer?.id ? "customer" : c?.lead?.id ? "lead" : null);

/* ---------- small pieces ---------- */
function DirIcon({ call }) {
  const info = outcomeInfo(call);
  const Icon = info.key === "voicemail" ? VmIcon : info.key === "missed" ? PhoneMissed : call.direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
  const title = info.key === "voicemail" ? "Voicemail" : info.key === "missed" ? "Missed" : call.direction === "inbound" ? "Inbound" : "Outbound";
  return (
    <span className={`calls-dir calls-dir--${info.tone}`} title={title} aria-label={title}>
      <Icon size={15} />
    </span>
  );
}

function NameLink({ call, onOpen, className = "" }) {
  const name = contactName(call);
  const number = counterpartyNumber(call);
  if (linkedRecord(call)) {
    return (
      <button type="button" className={`calls-link ${className}`} onClick={(e) => { e.stopPropagation(); onOpen(call); }} title="Open customer">
        {name || number || "Unknown"}
      </button>
    );
  }
  return <span className={className}>{name || number || "Unknown"}</span>;
}

function MediaIcons({ call }) {
  const items = [];
  if (hasRecording(call)) items.push(["rec", Mic, recordingProcessing(call) ? "Recording processing" : "Recording"]);
  if (hasTranscript(call)) items.push(["tx", FileText, "Transcript"]);
  if (hasCoaching(call)) items.push(["coach", GraduationCap, "Coaching card"]);
  if (!items.length) return null;
  return (
    <span className="calls-icons">
      {items.map(([k, Icon, title]) => <Icon key={k} size={14} aria-label={title}><title>{title}</title></Icon>)}
    </span>
  );
}

function FilterControls({ filters, setFilters, team, stacked = false }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className={`calls-filters ${stacked ? "calls-filters--stacked" : ""}`}>
      <Select aria-label="Direction" value={filters.direction} onChange={(e) => set("direction", e.target.value)}>
        <option value="">All directions</option>
        <option value="inbound">Inbound</option>
        <option value="outbound">Outbound</option>
      </Select>
      <Select aria-label="Outcome" value={filters.outcome} onChange={(e) => set("outcome", e.target.value)}>
        <option value="">All outcomes</option>
        {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Select>
      <Select aria-label="Rep" value={filters.user} onChange={(e) => set("user", e.target.value)}>
        <option value="">All reps</option>
        {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </Select>
      <Select aria-label="Date range" value={filters.range} onChange={(e) => set("range", e.target.value)}>
        {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Select>
      {filters.range === "custom" ? (
        <>
          <Input type="date" aria-label="From" value={filters.from} onChange={(e) => set("from", e.target.value)} />
          <Input type="date" aria-label="To" value={filters.to} onChange={(e) => set("to", e.target.value)} />
        </>
      ) : null}
      <Checkbox label="Has recording" checked={filters.hasRecording} onChange={(v) => set("hasRecording", v)} />
      <Checkbox label="Needs follow-up" checked={filters.needsFollowUp} onChange={(v) => set("needsFollowUp", v)} />
    </div>
  );
}

const QUICK_CHIPS = [
  { id: "all", label: "All", patch: { direction: "", outcome: "", needsFollowUp: false } },
  { id: "missed", label: "Missed", patch: { direction: "inbound", outcome: "no_answer", needsFollowUp: false } },
  { id: "voicemail", label: "Voicemail", patch: { direction: "", outcome: "voicemail", needsFollowUp: false } },
  { id: "followup", label: "Needs follow-up", patch: { direction: "", outcome: "", needsFollowUp: true } },
  { id: "inbound", label: "Inbound", patch: { direction: "inbound", outcome: "", needsFollowUp: false } },
  { id: "outbound", label: "Outbound", patch: { direction: "outbound", outcome: "", needsFollowUp: false } },
];
const chipOn = (chip, f) => Object.entries(chip.patch).every(([k, v]) => f[k] === v);

function Tiles({ today, vm }) {
  const s = today.state === "ready" ? todayStats(today.rows) : null;
  const capped = today.state === "ready" && today.rows.length >= TODAY_LIMIT;
  const whyNot = today.state === "error" ? "Couldn’t load today’s calls" : "Loading…";
  const tiles = [
    { label: "Calls today", value: s ? (capped ? `${TODAY_LIMIT}+` : s.calls) : "—", meta: s ? (capped ? `Newest ${TODAY_LIMIT} counted` : "Since midnight") : whyNot },
    { label: "Answered", value: s && s.answeredPct !== null ? `${s.answeredPct}%` : "—", meta: s ? (s.calls ? `${s.answered} of ${s.calls}` : "No calls yet today") : whyNot },
    { label: "Missed", value: s ? s.missed : "—", meta: s ? "Inbound, not answered" : whyNot },
    { label: "Voicemails waiting", value: vm.state === "ready" ? vm.n : "—", meta: vm.state === "ready" ? "Not yet handled" : vm.state === "error" ? "Couldn’t load the voicemail queue" : "Loading…" },
    { label: "Avg duration", value: s && s.avgDuration !== null ? fmtDuration(s.avgDuration) : "—", meta: s ? (s.avgDuration !== null ? "Answered calls today" : "No answered calls today") : whyNot },
  ];
  return (
    <div className="calls-tiles">
      {tiles.map((t) => (
        <CardBoundary key={t.label} name={`calls tile ${t.label}`}>
          <StatCard label={t.label} value={t.value} meta={t.meta} />
        </CardBoundary>
      ))}
    </div>
  );
}

/* ---------- detail rail ---------- */
function CallDetail({ call, onBack, onOpenProfile, onChanged }) {
  const navigate = useNavigate();
  const [full, setFull] = useState(call);
  const [outcome, setOutcome] = useState(call.outcome || "");
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");

  useEffect(() => {
    let live = true;
    setFull(call);
    setOutcome(call.outcome || "");
    setNotes("");
    setFollowUp("");
    (async () => {
      try {
        const d = await fetchCallDetail(call.call_sid || call.id);
        if (!live || !d) return;
        // The detail view stamps outcome="unknown" when it has none — keep ours.
        const { outcome: o, ...rest } = d;
        setFull((f) => ({ ...f, ...rest, outcome: o && o !== "unknown" ? o : f.outcome }));
      } catch { /* the list row is enough to render */ }
    })();
    return () => { live = false; };
  }, [call]);

  const sid = full.call_sid || "";
  const info = outcomeInfo(full);
  const number = counterpartyNumber(full);
  const name = contactName(full);
  const rep = repName(full);
  const linked = linkedRecord(full);

  const save = async () => {
    const payload = wrapUpPayload({ call: full, outcome, notes, followUpDate: followUp });
    // Inline, not a toast: the toast stack sits over this corner of the rail and
    // would cover the Save button for the next click.
    if (!payload) { setHint("Nothing to save yet — change the outcome, add a note or a follow-up date."); return; }
    setHint("");
    setSaving(true);
    try {
      await wrapUpCall(sid, payload);
      toast.success(payload.next_action ? "Wrap-up saved and follow-up task created" : "Wrap-up saved");
      setNotes("");
      setFollowUp("");
      if (payload.outcome) setFull((f) => ({ ...f, outcome: payload.outcome }));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn’t save the wrap-up");
    } finally {
      setSaving(false);
    }
  };

  const setHandled = async (handled) => {
    setBusy(true);
    try {
      const res = await triageCall(sid, { handled });
      setFull((f) => ({ ...f, handled_at: res.handled_at, handled_by_name: res.handled_by_name }));
      toast.success(handled ? "Marked handled" : "Reopened");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn’t update the call");
    } finally {
      setBusy(false);
    }
  };

  const card = full.coaching && typeof full.coaching === "object" ? full.coaching : {};
  const ratio = card.talk_ratio && typeof card.talk_ratio === "object" ? card.talk_ratio : null;

  return (
    <div className="cdet" data-call={sid || full.id}>
      <div className="cdet__head">
        {onBack ? (
          <Button variant="ghost" iconOnly aria-label="Back to calls" onClick={onBack}><ArrowLeft size={18} /></Button>
        ) : null}
        <div className="cdet__who">
          <NameLink call={full} onOpen={onOpenProfile} className="cdet__name" />
          {name && number ? <span className="cdet__sub">{number}</span> : null}
        </div>
        <Badge tone={info.tone}>{info.label}</Badge>
      </div>
      <div className="cdet__meta">
        <span>{full.direction === "inbound" ? "Inbound" : "Outbound"}</span>
        <span>{fmtFull(full.created_at) || "—"}</span>
        <span className="calls-dur">{fmtDuration(full.duration_seconds)}</span>
        <span>{rep ? `Rep ${rep}` : "Rep —"}</span>
      </div>
      <div className="cdet__actions">
        <ClickToCallButton phone={number} calleeName={name} label="Call back" />
        <Button size="sm" icon={<MessageSquare size={14} />} onClick={() => navigate(`/communication/sms?to=${encodeURIComponent(number)}`)} disabled={!number}>
          Text
        </Button>
        {linked ? (
          <Button size="sm" icon={<Users size={14} />} onClick={() => onOpenProfile(full)}>Open customer</Button>
        ) : (
          <Button size="sm" icon={<UserPlus size={14} />} onClick={() => navigate(`/customers/list?new=1&phone=${encodeURIComponent(number)}`)}>
            Create contact
          </Button>
        )}
      </div>

      <CardBoundary name="call recording">
        <section className="cdet-card">
          <div className="cdet-card__head">{full.voicemail || info.key === "voicemail" ? "Voicemail" : "Recording"}</div>
          <div className="cdet-card__body">
            {full.recording_playback_url ? (
              <CallPlayer key={sid} src={full.recording_playback_url} label={full.voicemail ? "voicemail" : "recording"} />
            ) : recordingNeedsFetch(full) ? (
              // The detail read has not answered (or did not presign): sign on Play.
              <CallPlayer key={sid} getSrc={() => fetchRecordingUrl(sid)} label={full.voicemail ? "voicemail" : "recording"} />
            ) : recordingProcessing(full) ? (
              <p className="cdet-why">Recording is still being stored — check back in a minute.</p>
            ) : (
              <p className="cdet-why">No recording for this call.</p>
            )}
          </div>
        </section>
      </CardBoundary>

      <CardBoundary name="call transcript">
        <section className="cdet-card">
          <div className="cdet-card__head">Transcript</div>
          <div className="cdet-card__body">
            {full.ai_summary && !String(full.ai_summary).toLowerCase().startsWith("call failed:") ? (
              <p className="cdet-summary">{full.ai_summary}</p>
            ) : null}
            {hasTranscript(full) ? (
              <CallTranscript text={full.transcript} />
            ) : (
              <p className="cdet-why">
                {hasRecording(full)
                  ? "No transcript yet. Transcripts are made from the stored recording, only where call recording is disclosed."
                  : "No transcript — this call has no recording."}
              </p>
            )}
          </div>
        </section>
      </CardBoundary>

      {hasCoaching(full) ? (
        <CardBoundary name="call coaching">
          <section className="cdet-card coach">
            <div className="cdet-card__head">Coaching</div>
            <div className="cdet-card__body">
              {card.who ? <div className="coach__row"><span className="coach__k">Who</span><span className="coach__v">{card.who}</span></div> : null}
              {card.what_they_do ? <div className="coach__row"><span className="coach__k">What they do</span><span className="coach__v">{card.what_they_do}</span></div> : null}
              {card.objection ? (
                <div className="coach__row">
                  <span className="coach__k">Objection</span>
                  <span className="coach__v">{card.objection}</span>
                  {card.objection_words ? <q className="coach__quote">{card.objection_words}</q> : null}
                </div>
              ) : (
                <div className="coach__row"><span className="coach__k">Objection</span><span className="coach__v cdet-why">None raised</span></div>
              )}
              {card.reply ? <div className="coach__row"><span className="coach__k">What you said</span><span className="coach__v">{card.reply}</span></div> : null}
              {card.better_reply ? <div className="coach__row"><span className="coach__k">A better reply</span><span className="coach__v">{card.better_reply}</span></div> : null}
              {ratio && Number.isFinite(ratio.rep_pct) ? (
                <div className="coach__row">
                  <span className="coach__k">Talk ratio</span>
                  <span className="coach__v">You {ratio.rep_pct}% · them {ratio.caller_pct}%</span>
                  <span className="coach__ratio" aria-hidden="true"><span className="coach__ratio-rep" style={{ width: `${ratio.rep_pct}%` }} /></span>
                </div>
              ) : null}
              {typeof card.asked_for_next === "boolean" ? (
                <div className="coach__row"><span className="coach__k">Asked for a next step</span><span className="coach__v">{card.asked_for_next ? "Yes" : "No"}</span></div>
              ) : null}
              {card.do_differently ? <div className="coach__row"><span className="coach__k">Next time</span><span className="coach__v">{card.do_differently}</span></div> : null}
            </div>
          </section>
        </CardBoundary>
      ) : null}

      <CardBoundary name="call wrap-up">
        <section className="cdet-card">
          <div className="cdet-card__head">
            Wrap-up
            {full.handled_at ? (
              <span className="wrap__handled">Handled{full.handled_by_name ? ` by ${full.handled_by_name}` : ""}</span>
            ) : null}
          </div>
          <div className="cdet-card__body">
            {!sid ? (
              <p className="cdet-why">This call has no provider id yet, so it can’t be wrapped up.</p>
            ) : (
              <>
                <div className="wrap">
                  <Field label="Outcome">
                    {(id) => (
                      <Select id={id} value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                        <option value="">Not set</option>
                        {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{v === "no_answer" ? (full.direction === "inbound" ? "Missed" : "No answer") : l}</option>)}
                      </Select>
                    )}
                  </Field>
                  <Field label="Follow-up task date" hint={followUp ? "Creates a task on save" : undefined}>
                    {(id) => <Input id={id} type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />}
                  </Field>
                  <Field label="Note" className="wrap__span2">
                    {(id) => <Textarea id={id} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was agreed, promised or asked" />}
                  </Field>
                </div>
                {hint ? <p className="cdet-why wrap__hint" role="status">{hint}</p> : null}
                <div className="wrap__actions">
                  {full.handled_at ? (
                    <Button size="sm" variant="ghost" onClick={() => setHandled(false)} disabled={busy}>Reopen</Button>
                  ) : (
                    <Button size="sm" onClick={() => setHandled(true)} disabled={busy}>Mark handled</Button>
                  )}
                  {/* One accent fill per screen: Call back (above) is it; Save is secondary. */}
                  <Button size="sm" onClick={save} loading={saving}>Save</Button>
                </div>
              </>
            )}
          </div>
        </section>
      </CardBoundary>
    </div>
  );
}

/* ---------- page ---------- */
export default function PhonePage() {
  const isPhone = useMedia("(max-width: 640px)");
  const compact = useMedia("(max-width: 820px)");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [listState, setListState] = useState("loading");
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const moreRef = useRef(null);
  const [today, setToday] = useState({ state: "loading", rows: [] });
  const [vm, setVm] = useState({ state: "loading", n: null });
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialOpen, setDialOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [profileFor, setProfileFor] = useState(null);
  const bodyRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(null);

  // Like the inbox: size the two panes to the space below the chrome, measured, so
  // the rail is never pushed under the fold. Phone width scrolls the page instead.
  useEffect(() => {
    if (isPhone) { setBodyHeight(null); return undefined; }
    const measure = () => {
      if (!bodyRef.current) return;
      const top = bodyRef.current.getBoundingClientRect().top + window.scrollY;
      setBodyHeight(Math.max(420, window.innerHeight - top - 16));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isPhone, today.state]);

  const listParams = useCallback(() => {
    const params = { limit: PAGE, ...dateRangeParams(filters.range, filters.from, filters.to) };
    if (filters.direction) params.direction = filters.direction;
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.user) params.user = filters.user;
    if (filters.needsFollowUp) { params.queue = "review"; params.handled = "false"; }
    return params;
  }, [filters]);

  const sortDesc = (list) => [...list].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  // /comms/calls/ has no offset — it pages by date: the next page is everything at or
  // before the oldest row shown (?end=), de-duplicated on the call id.
  const loadList = useCallback(async () => {
    setListState("loading");
    setMore(false);
    try {
      const raw = asList(await fetchCallLogs(listParams()));
      setRows(sortDesc(raw.filter((c) => !isChildLeg(c))));
      setMore(raw.length >= PAGE);
      setListState("ready");
    } catch {
      setListState("error");
    }
  }, [listParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !more || !rows.length) return;
    setLoadingMore(true);
    try {
      const oldest = rows[rows.length - 1].created_at;
      const raw = asList(await fetchCallLogs({ ...listParams(), end: oldest }));
      const seen = new Set(rows.map(callKey));
      const fresh = raw.filter((c) => !isChildLeg(c) && !seen.has(callKey(c)));
      setRows((r) => sortDesc([...r, ...fresh]));
      setMore(raw.length >= PAGE && fresh.length > 0);
    } catch {
      toast.error("Couldn’t load older calls");
      setMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, more, rows, listParams]);

  // Older pages load when the end of the list scrolls into view (works for the
  // desktop pane and the phone's page scroll alike); the button is the fallback.
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !more || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [more, loadMore, listState, isPhone]);

  // Never a blank list: past SLOW_AFTER_S the skeleton says how long it has waited.
  useEffect(() => {
    if (listState !== "loading") { setElapsed(0); return undefined; }
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [listState]);

  const loadStats = useCallback(async () => {
    setToday((t) => ({ ...t, state: "loading" }));
    setVm((v) => ({ ...v, state: "loading" }));
    const [t, v] = await Promise.allSettled([
      fetchCallLogs({ ...dateRangeParams("today"), limit: TODAY_LIMIT }),
      fetchCallLogs({ queue: "review", handled: "false", limit: 500 }),
    ]);
    setToday(t.status === "fulfilled" ? { state: "ready", rows: asList(t.value) } : { state: "error", rows: [] });
    if (v.status === "fulfilled") {
      const n = asList(v.value).filter((c) => !isChildLeg(c) && outcomeInfo(c).key === "voicemail").length;
      setVm({ state: "ready", n });
    } else {
      setVm({ state: "error", n: null });
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    let live = true;
    listTeamMembers()
      .then((res) => {
        const list = asList(res?.data ?? res);
        if (!live) return;
        setTeam(list.map((u) => ({ id: u.id, name: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || `User ${u.id}` })));
      })
      .catch(() => { /* rep filter just stays empty */ });
    return () => { live = false; };
  }, []);

  const visible = useMemo(
    () => rows.filter((c) => (!filters.hasRecording || hasRecording(c)) && matchesQuery(c, q)),
    [rows, filters.hasRecording, q]
  );
  const filtering = q.trim() || Object.keys(EMPTY_FILTERS).some((k) => filters[k] !== EMPTY_FILTERS[k]);
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setQ(""); };

  const openProfile = useCallback((c) => {
    if (c?.customer?.id) {
      setProfileFor({ kind: "customer", rec: { object: "customer", id: c.customer.id, customer_id: c.customer.id, universal_id: c.customer.universal_id || c.party_universal_id || undefined, display_name: contactName(c), contact_phone: counterpartyNumber(c) } });
    } else if (c?.lead?.id) {
      setProfileFor({ kind: "lead", rec: { object: "lead", id: c.lead.id, crm_lead_id: c.lead.id, universal_id: c.lead.universal_id || c.party_universal_id || undefined, display_name: contactName(c), contact_phone: counterpartyNumber(c) } });
    } else {
      toast.info("No linked customer or lead on this call yet");
    }
  }, []);

  const refreshAll = () => { loadList(); loadStats(); };
  const selectedKey = callKey(selected);
  const showDetail = Boolean(selected);

  const listBody = () => {
    if (listState === "loading") {
      return (
        <div className="calls-skel" aria-busy="true">
          <Skeleton variant="row" count={8} />
          {elapsed >= SLOW_AFTER_S ? <p className="calls-slow" role="status">Loading calls… {elapsed}s</p> : null}
        </div>
      );
    }
    if (listState === "error") {
      return <EmptyState title="Couldn’t load calls" body="The call log didn’t answer. Try again." action={<Button size="sm" onClick={loadList}>Retry</Button>} />;
    }
    if (!visible.length) {
      return filtering ? (
        <EmptyState title="No calls match" body="Clear a filter or widen the date range." action={<Button size="sm" onClick={clearFilters}>Clear filters</Button>} />
      ) : (
        <EmptyState title="No calls yet" body="Dial someone from the keypad, or the first inbound call lands here." action={<Button size="sm" onClick={() => setDialOpen(true)}>Dial</Button>} />
      );
    }
    if (isPhone) {
      return (
        <div className="calls-phone-list">
          {visible.map((c) => {
            const info = outcomeInfo(c);
            return (
              <button type="button" className={`calls-row ${selectedKey === callKey(c) ? "is-selected" : ""}`} key={callKey(c)} onClick={() => setSelected(c)}>
                <DirIcon call={c} />
                <span className="calls-row__main">
                  <span className="calls-row__name">{contactName(c) || counterpartyNumber(c) || "Unknown"}</span>
                  <span className="calls-row__sub">
                    <Badge tone={info.tone}>{info.label}</Badge>
                    {contactName(c) && counterpartyNumber(c) ? <span className="calls-num">{counterpartyNumber(c)}</span> : null}
                    <MediaIcons call={c} />
                  </span>
                </span>
                <span className="calls-row__right">
                  <span className="calls-time">{fmtWhen(c.created_at)}</span>
                  <span className="calls-dur">{fmtDuration(c.duration_seconds)}</span>
                </span>
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <Table className="calls-table">
        <thead>
          <tr>
            <th>Time</th>
            <th aria-label="Direction" />
            <th>Who</th>
            <th>Outcome</th>
            <th className="is-num">Duration</th>
            <th>Rep</th>
            <th aria-label="Recording, transcript, coaching" />
          </tr>
        </thead>
        <tbody>
          {visible.map((c) => {
            const info = outcomeInfo(c);
            const rep = repName(c);
            return (
              <TRow key={callKey(c)} className={selectedKey === callKey(c) ? "is-selected" : ""} onClick={() => setSelected(c)} aria-selected={selectedKey === callKey(c)}>
                <TCell><span className="calls-time" title={fmtFull(c.created_at)}>{fmtWhen(c.created_at)}</span></TCell>
                <TCell><DirIcon call={c} /></TCell>
                <TCell>
                  <NameLink call={c} onOpen={openProfile} />
                  {contactName(c) && counterpartyNumber(c) ? <span className="calls-num calls-num--inline">{counterpartyNumber(c)}</span> : null}
                </TCell>
                <TCell><Badge tone={info.tone}>{info.label}</Badge></TCell>
                <TCell num><span className="calls-dur">{fmtDuration(c.duration_seconds)}</span></TCell>
                <TCell>{rep || <span className="calls-unknown" title="No rep on this call">—</span>}</TCell>
                <TCell><MediaIcons call={c} /></TCell>
              </TRow>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <div className={`calls ${showDetail ? "calls--detail" : ""} ${isPhone ? "calls--phone" : ""}`}>
      <PageHeader
        title="Calls"
        subtitle={isPhone ? undefined : "Every call on your numbers, newest first."}
        secondary={<Button icon={<Phone size={14} />} onClick={() => setDialOpen(true)}>Dial</Button>}
      />
      {!isPhone ? <Tiles today={today} vm={vm} /> : null}

      <div className="calls-body" ref={bodyRef} style={bodyHeight ? { height: bodyHeight } : undefined}>
        <section className="calls-list" aria-label="Call list">
          <div className="calls-list__head">
            <div className="calls-list__search">
              <Input className="calls-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or number" aria-label="Search calls by name or number" inputMode="search" />
              {isPhone ? (
                <Button variant="ghost" iconOnly aria-label="Filters" onClick={() => setFiltersOpen(true)}><SlidersHorizontal size={16} /></Button>
              ) : null}
            </div>
            {isPhone ? (
              <div className="calls-chips" role="group" aria-label="Quick filters">
                {QUICK_CHIPS.map((chip) => (
                  <button key={chip.id} type="button" className={`calls-chip ${chipOn(chip, filters) ? "is-on" : ""}`} onClick={() => setFilters((f) => ({ ...f, ...chip.patch }))}>
                    {chip.label}
                  </button>
                ))}
              </div>
            ) : (
              <FilterControls filters={filters} setFilters={setFilters} team={team} />
            )}
          </div>
          <div className="calls-rows">
            <CardBoundary name="call list">{listBody()}</CardBoundary>
            {listState === "ready" && more ? (
              <div className="calls-more" ref={moreRef}>
                <Button size="sm" variant="ghost" onClick={loadMore} loading={loadingMore}>
                  {loadingMore ? "Loading older calls…" : "Load older calls"}
                </Button>
              </div>
            ) : null}
          </div>
        </section>

        <aside className="calls-rail" aria-label="Call detail">
          {selected ? (
            <CardBoundary name="call detail">
              <CallDetail
                key={selectedKey}
                call={selected}
                onBack={compact ? () => setSelected(null) : null}
                onOpenProfile={openProfile}
                onChanged={refreshAll}
              />
            </CardBoundary>
          ) : (
            <EmptyState title="Pick a call" body="Its recording, transcript, coaching and wrap-up show here." />
          )}
        </aside>
      </div>

      <DialSheet open={dialOpen} onClose={() => setDialOpen(false)} isPhone={isPhone} />

      {isPhone ? (
        <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters"
          footer={<><Button variant="ghost" onClick={clearFilters}>Clear</Button><Button variant="primary" onClick={() => setFiltersOpen(false)}>Done</Button></>}>
          <FilterControls filters={filters} setFilters={setFilters} team={team} stacked />
        </Sheet>
      ) : null}

      {profileFor ? (
        <Suspense fallback={null}>
          <CustomerPopup lead={profileFor.rec} leadType={profileFor.kind} onClose={() => setProfileFor(null)} />
        </Suspense>
      ) : null}
    </div>
  );
}
